import { NetworkTelemetry, SourceMetadata } from "../types";

export interface ParsedBrowserOs {
  browser: string;
  browserName: string;
  browserVersion: string;
  os: string;
  osName: string;
  osVersion: string;
}

/**
 * Parses user agent string and extracts detailed browser and OS names and versions.
 * e.g., "Chrome 129.0.0", "Safari Mobile 18.0", "macOS 14.5", "Windows 10/11", "iOS 18.0".
 */
export function parseBrowserAndOs(userAgent: string): ParsedBrowserOs {
  let browserName = "Unknown Browser";
  let browserVersion = "";
  let osName = "Unknown OS";
  let osVersion = "";

  if (!userAgent) {
    return {
      browser: browserName,
      browserName,
      browserVersion,
      os: osName,
      osName,
      osVersion,
    };
  }

  // --- 1. OS Detection & Version Extraction ---
  if (/Windows/i.test(userAgent)) {
    osName = "Windows";
    const ntMatch = userAgent.match(/Windows NT (\d+\.\d+)/i);
    if (ntMatch && ntMatch[1]) {
      const ntVer = ntMatch[1];
      if (ntVer === "10.0") osVersion = "10/11";
      else if (ntVer === "6.3") osVersion = "8.1";
      else if (ntVer === "6.2") osVersion = "8";
      else if (ntVer === "6.1") osVersion = "7";
      else osVersion = ntVer;
    }
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    const isPad = /iPad/i.test(userAgent);
    osName = isPad ? "iPadOS" : "iOS";
    const iosMatch = userAgent.match(/OS (\d+[._]\d+([._]\d+)?)/i);
    if (iosMatch && iosMatch[1]) {
      osVersion = iosMatch[1].replace(/_/g, ".");
    }
  } else if (/Macintosh|Mac OS X/i.test(userAgent)) {
    osName = "macOS";
    const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+([._]\d+)?)/i);
    if (macMatch && macMatch[1]) {
      osVersion = macMatch[1].replace(/_/g, ".");
    }
  } else if (/Android/i.test(userAgent)) {
    osName = "Android";
    const androidMatch = userAgent.match(/Android (\d+(\.\d+)*)/i);
    if (androidMatch && androidMatch[1]) {
      osVersion = androidMatch[1];
    }
  } else if (/CrOS/i.test(userAgent)) {
    osName = "ChromeOS";
    const crMatch = userAgent.match(/CrOS [^\s]+ (\d+(\.\d+)*)/i);
    if (crMatch && crMatch[1]) {
      osVersion = crMatch[1];
    }
  } else if (/Linux/i.test(userAgent)) {
    osName = "Linux";
  }

  // --- 2. Browser Detection & Version Extraction ---
  const isMobile = /Mobile|iPhone|iPad|iPod|Android/i.test(userAgent);

  // Microsoft Edge (Chromium or Legacy)
  const edgeMatch = userAgent.match(/Edg(?:e|A|iOS)?\/(\d+(\.\d+)*)/i);
  // Opera
  const operaMatch = userAgent.match(/(?:OPR|Opera)\/(\d+(\.\d+)*)/i);
  // Samsung Browser
  const samsungMatch = userAgent.match(/SamsungBrowser\/(\d+(\.\d+)*)/i);
  // Firefox
  const firefoxMatch = userAgent.match(/(?:Firefox|FxiOS)\/(\d+(\.\d+)*)/i);
  // Chrome / Chromium / CriOS
  const chromeMatch = userAgent.match(/(?:Chrome|CriOS)\/(\d+(\.\d+)*)/i);
  // Safari
  const safariVersionMatch = userAgent.match(/Version\/(\d+(\.\d+)*)/i);

  if (edgeMatch && edgeMatch[1]) {
    browserName = "Edge";
    browserVersion = edgeMatch[1];
  } else if (operaMatch && operaMatch[1]) {
    browserName = "Opera";
    browserVersion = operaMatch[1];
  } else if (samsungMatch && samsungMatch[1]) {
    browserName = "Samsung Internet";
    browserVersion = samsungMatch[1];
  } else if (firefoxMatch && firefoxMatch[1]) {
    browserName = "Firefox";
    browserVersion = firefoxMatch[1];
  } else if (chromeMatch && chromeMatch[1] && !/Edg/i.test(userAgent)) {
    browserName = "Chrome";
    browserVersion = chromeMatch[1];
  } else if (/Safari/i.test(userAgent) && !chromeMatch) {
    browserName = isMobile ? "Safari Mobile" : "Safari";
    if (safariVersionMatch && safariVersionMatch[1]) {
      browserVersion = safariVersionMatch[1];
    } else {
      const safariBuildMatch = userAgent.match(/Safari\/(\d+(\.\d+)*)/i);
      browserVersion =
        safariBuildMatch && safariBuildMatch[1] ? safariBuildMatch[1] : "";
    }
  } else if (/Trident|MSIE/i.test(userAgent)) {
    browserName = "Internet Explorer";
    const ieMatch = userAgent.match(/(?:MSIE |rv:)(\d+(\.\d+)*)/i);
    if (ieMatch && ieMatch[1]) {
      browserVersion = ieMatch[1];
    }
  }

  const browser = browserVersion
    ? `${browserName} ${browserVersion}`
    : browserName;
  const os = osVersion ? `${osName} ${osVersion}` : osName;

  return {
    browser,
    browserName,
    browserVersion,
    os,
    osName,
    osVersion,
  };
}

/**
 * Extracts network connectivity and performance telemetry non-destructively.
 */
export function getNetworkTelemetry(): NetworkTelemetry {
  const isBrowser = typeof window !== "undefined";
  if (!isBrowser) {
    return { online: true };
  }

  const nav = window.navigator;
  const online = nav.onLine ?? true;

  // Connection API (Network Information API)
  const connection =
    (
      nav as unknown as {
        connection?: {
          effectiveType?: string;
          downlink?: number;
          rtt?: number;
          saveData?: boolean;
        };
      }
    ).connection ||
    (
      nav as unknown as {
        mozConnection?: {
          effectiveType?: string;
          downlink?: number;
          rtt?: number;
          saveData?: boolean;
        };
      }
    ).mozConnection ||
    (
      nav as unknown as {
        webkitConnection?: {
          effectiveType?: string;
          downlink?: number;
          rtt?: number;
          saveData?: boolean;
        };
      }
    ).webkitConnection;

  const validTypes: Array<NetworkTelemetry["effectiveType"]> = [
    "slow-2g",
    "2g",
    "3g",
    "4g",
  ];
  const effectiveType =
    connection?.effectiveType &&
    validTypes.includes(
      connection.effectiveType as NetworkTelemetry["effectiveType"],
    )
      ? (connection.effectiveType as NetworkTelemetry["effectiveType"])
      : undefined;

  return {
    online,
    effectiveType,
    downlink:
      typeof connection?.downlink === "number"
        ? connection.downlink
        : undefined,
    rtt: typeof connection?.rtt === "number" ? connection.rtt : undefined,
    saveData:
      typeof connection?.saveData === "boolean"
        ? connection.saveData
        : undefined,
  };
}

/**
 * Safely fetches client public IP address with a fast timeout (default 1500ms).
 * Returns null if network fails, times out, or is offline.
 */
export async function fetchClientIp(
  timeoutMs = 1500,
  endpoint = "https://api.ipify.org?format=json",
): Promise<string | null> {
  if (
    typeof window === "undefined" ||
    (window.navigator && !window.navigator.onLine)
  ) {
    return null;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(endpoint, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { ip?: string };
    return typeof data?.ip === "string" ? data.ip.trim() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Formats network telemetry for user display.
 * e.g., "Online (4G, ~10 Mbps, 50ms RTT)" or "Offline".
 */
export function formatNetworkStatus(telemetry?: NetworkTelemetry): string {
  if (!telemetry) return "Unknown";
  if (!telemetry.online) return "Offline";

  const details: string[] = [];
  if (telemetry.effectiveType) {
    details.push(telemetry.effectiveType.toUpperCase());
  }
  if (typeof telemetry.downlink === "number" && telemetry.downlink > 0) {
    details.push(`~${telemetry.downlink} Mbps`);
  }
  if (typeof telemetry.rtt === "number" && telemetry.rtt > 0) {
    details.push(`${telemetry.rtt}ms RTT`);
  }

  return details.length > 0 ? `Online (${details.join(", ")})` : "Online";
}

/**
 * Gathers complete diagnostic telemetry for an issue submission.
 */
export function gatherSourceMetadata(options?: {
  url?: string;
  ipAddress?: string | null;
}): SourceMetadata {
  const isBrowser = typeof window !== "undefined";
  const userAgent = isBrowser
    ? window.navigator.userAgent
    : "Node.js Environment";
  const { browser, browserName, browserVersion, os, osName, osVersion } =
    parseBrowserAndOs(userAgent);

  const screenResolution =
    isBrowser && window.screen
      ? `${window.screen.width}x${window.screen.height}`
      : "Unknown";
  const viewportSize = isBrowser
    ? `${window.innerWidth}x${window.innerHeight}`
    : "Unknown";
  const network = getNetworkTelemetry();

  return {
    url:
      options?.url ||
      (isBrowser ? window.location.href : "https://mlconnections.com"),
    pageTitle: isBrowser ? document.title || "MLC Ticketing" : "MLC Ticketing",
    userAgent,
    browser,
    browserName,
    browserVersion,
    os,
    osName,
    osVersion,
    screenResolution,
    viewportSize,
    ipAddress: options?.ipAddress || null,
    network,
  };
}
